import { isShipChandlingLifeboatsWorkflowService } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import { STATUS_KEYS } from "@features/vessel-call/vessel-call-status/PortServices/preview/statusKeys";
import type { Resource } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import type { PortServiceResourceEquipment } from "@services/port-services/types";
import { approveByMDShipToShipSchema } from "../schemas/approveByMDShipToShipSchema";
import { approveByOperationsLoadingAndOffloadingSchema } from "../schemas/approveByOperationsLoadingAndOffloadingSchema";
import { approveByOperationsShipChandlingSchema } from "../schemas/approveByOperationsShipChandlingSchema";
import { closeLoadingAndOffloadingSchema } from "../schemas/closeLoadingAndOffloadingSchema";
import { closeShipChandlingSchema } from "../schemas/closeShipChandlingSchema";
import { rejectionSchema } from "../schemas/rejectionSchema";
import { startLoadingAndOffloadingSchema } from "../schemas/startLoadingAndOffloaingSchema";
import { startShipToShipSchema } from "../schemas/startShipToShipSchema";
import type { PortServiceFlow } from "../waste-disposal/utils";

const LIFEBOATS_ALLOWED_ROLE_KEYS = ["marine-operator", "marine-supervisor"];

export const SHIP_CHANDLING_LOADING_AND_OFFLOADING_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: ["Operations"],
    nextStatus: STATUS_KEYS.OPERATIONS_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent", "Marine Operator", "Marine Supervisor"],
    statusesList: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: {
        label: "Approved by Operations",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortServicesShipChandling",
        extraFields: ["notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortServicesShipChandling",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent", "Marine Operator", "Marine Supervisor"],
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortServicesShipChandling",
        extraFields: ["ats", "etc"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent", "Marine Operator", "Marine Supervisor"],
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor", "operations"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortServicesShipChandling",
        extraFields: ["atc", "numberOfPackagesHandled", "spares", "incidents", "resources"],
      },
    },
  },
};

export const SHIP_CHANDLING_LIFEBOATS_FLOW: PortServiceFlow = {
  [STATUS_KEYS.PENDING_APPROVAL]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: ["Operations", "MD"],
    nextStatus: STATUS_KEYS.OPERATIONS_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Marine Operator", "Marine Supervisor"],
    statusesList: {
      [STATUS_KEYS.OPERATIONS_APPROVED]: {
        label: "Approved by Operations",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["chargedParty", "notes", "attachment"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.OPERATIONS_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["MD"],
    canReject: ["MD"],
    nextStatus: STATUS_KEYS.MD_APPROVED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Dry Bulk"],
    statusesList: {
      [STATUS_KEYS.MD_APPROVED]: {
        label: "Approved by Marine Department",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canApprovePortService",
        extraFields: ["externalNotes", "internalNotes"],
      },
      [STATUS_KEYS.REJECTED]: {
        label: "Rejected",
        badgeType: "error",
        canBeSelected: true,
        actionPermission: "canRejectPortService",
        extraFields: ["rejectionReason"],
      },
    },
  },
  [STATUS_KEYS.MD_APPROVED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: [],
    nextStatus: STATUS_KEYS.STARTED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent", "Marine Operator", "Marine Supervisor"],
    statusesList: {
      [STATUS_KEYS.STARTED]: {
        label: "Started",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canStartPortService",
        extraFields: ["ats", "etc"],
      },
    },
  },
  [STATUS_KEYS.STARTED]: {
    serviceRequester: "Shipping Agent",
    canApprove: ["Operations"],
    canReject: [],
    nextStatus: STATUS_KEYS.CLOSED,
    nonApproverStatus: STATUS_KEYS.REJECTED,
    defaultNotifyRoles: ["Shipping Agent", "Marine Operator", "Marine Supervisor"],
    canCancel: [
      {
        userRoles: ["marine-operator", "marine-supervisor", "operations"],
        permission: "canCancelPortService",
      },
    ],
    statusesList: {
      [STATUS_KEYS.CLOSED]: {
        label: "Closed",
        badgeType: "success",
        canBeSelected: true,
        actionPermission: "canClosePortService",
        extraFields: ["atc", "numberOfPackagesHandled", "spares", "incidents", "resources"],
      },
    },
  },
};

export const mapLoadingAndOffloadingResourcesToShipChandlingResources = (
  resources?: Resource[],
): PortServiceResourceEquipment[] =>
  (resources ?? []).map((resource) => ({
    resourceTypeCode: resource.resourceTypeCode,
    resourceTypeName: resource.resourceTypeName,
    numberOfResources: resource.numberOfResources,
    startTimeOfUsage: resource.usageStartTime || null,
    endTimeOfUsage: resource.usageEndTime || null,
  }));

export const validateLoadingAndOffloadingExtraFields = ({
  newStatusOption,
  notifyOption,
  notes,
  attachment,
  rejectionReason,
  flowAts,
  flowEtc,
  flowAtc,
  numberOfPackagesHandled,
  spares,
  incidents,
  resources,
}: {
  newStatusOption: string;
  notifyOption: string[];
  notes: string;
  attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  numberOfPackagesHandled?: number;
  spares?: number;
  incidents?: string;
  resources?: Resource[];
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.OPERATIONS_APPROVED:
      return approveByOperationsLoadingAndOffloadingSchema().safeParse({
        notificationOption: notifyOption,
        notes,
        attachment,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startLoadingAndOffloadingSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeLoadingAndOffloadingSchema(flowAts).safeParse({
        atc: flowAtc,
        notificationOption: notifyOption,
        numberOfPackagesHandled,
        spares,
        incidents,
        resources,
      });

    default:
      return null;
  }
};

export const validateShipChandlingLifeboatsExtraFields = ({
  newStatusOption,
  notifyOption,
  chargedParty,
  notes,
  rejectionReason,
  flowAts,
  flowEtc,
  flowAtc,
  numberOfPackagesHandled,
  spares,
  incidents,
  resources,
  attachment,
  internalNotes,
  externalNotes,
}: {
  newStatusOption: string;
  notifyOption: string[];
  chargedParty: string;
  notes: string;
  rejectionReason: string;
  flowAts: string | Date | null;
  flowEtc: string | Date | null;
  flowAtc: string | Date | null;
  numberOfPackagesHandled?: number;
  spares?: number;
  incidents?: string;
  resources?: Resource[];
  attachment: {
    documentUrl: string;
    documentName: string;
    fileType: string;
  };
  internalNotes: string;
  externalNotes: string;
}) => {
  switch (newStatusOption) {
    case STATUS_KEYS.OPERATIONS_APPROVED:
      return approveByOperationsShipChandlingSchema().safeParse({
        chargedParty,
        notes,
        attachment,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.MD_APPROVED:
      return approveByMDShipToShipSchema().safeParse({
        internalNotes,
        externalNotes,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.REJECTED:
      return rejectionSchema().safeParse({
        rejectionReason: rejectionReason || "",
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.STARTED:
      return startShipToShipSchema().safeParse({
        ats: flowAts,
        etc: flowEtc,
        notificationOption: notifyOption,
      });

    case STATUS_KEYS.CLOSED:
      return closeShipChandlingSchema(flowAts).safeParse({
        atc: flowAtc,
        numberOfPackagesHandled,
        spares,
        incidents,
        resourcesAndEquipment: mapLoadingAndOffloadingResourcesToShipChandlingResources(resources),
        notificationOption: notifyOption,
      });

    default:
      return null;
  }
};

export const isShipChandling = (category: string) => {
  return category === "Ship Chandling";
};

export const isLoadingAndOffloadingType = (category: string, type: string) => {
  return (
    isShipChandling(category) &&
    type === "Loading & offloading of spares , stores, lube oil and General items"
  );
};

export const isShipChandlingLifeboatsWorkflowType = (category: string, type: string) => {
  return isShipChandlingLifeboatsWorkflowService(category, type);
};

export const isRestrictedLifeboatsMDUser = ({
  category,
  type,
  currentUserType,
  currentUserRoles,
}: {
  category: string;
  type: string;
  currentUserType: string;
  currentUserRoles: string[];
}) => {
  if (!isShipChandlingLifeboatsWorkflowService(category, type) || currentUserType !== "MD") {
    return false;
  }

  return !currentUserRoles.some((role) => LIFEBOATS_ALLOWED_ROLE_KEYS.includes(role));
};
